import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, Users, MapPin, Phone, Mail, 
  Check, X, MoreHorizontal, Search, Filter,
  MessageSquare, AlertCircle, Settings, CreditCard,
  ChevronLeft, ChevronRight, Eye, UserCheck, UserX,
  Download, FileSpreadsheet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isToday, parseISO } from 'date-fns';

const StatusBadge = ({ status }) => {
  const styles = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    confirmed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    checked_in: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    no_show: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
  };
  
  return (
    <Badge className={styles[status] || styles.pending}>
      {status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
    </Badge>
  );
};

const PartnerReservations = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPartner, user, loading: authLoading, refreshUser } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('calendar');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  
  // Calendar state
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Settings state
  const [settings, setSettings] = useState({
    require_card_hold: true,
    no_show_fee: 25,
    cancellation_hours: 24,
    advance_booking_days: 30,
    confirmation_message: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      
      if (!user) {
        try {
          await refreshUser();
        } catch (error) {
          navigate('/auth');
          return;
        }
      }
      
      if (!isPartner) {
        navigate('/dashboard');
        return;
      }
      
      fetchData();
    };
    
    init();
  }, [isAuthenticated, isPartner, authLoading, user]);

  const fetchData = async () => {
    try {
      const [reservationsRes, inquiriesRes, venuesRes] = await Promise.all([
        api.get('/partner/reservations'),
        api.get('/partner/inquiries'),
        api.get('/partner/venues')
      ]);
      setReservations(reservationsRes.data || []);
      setInquiries(inquiriesRes.data || []);
      setVenues(venuesRes.data || []);
      
      // Set first venue as selected if available
      if (venuesRes.data?.length > 0 && !selectedVenue) {
        setSelectedVenue(venuesRes.data[0]);
        fetchVenueSettings(venuesRes.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load reservations');
    } finally {
      setLoading(false);
    }
  };

  const fetchVenueSettings = async (venueId) => {
    try {
      const response = await api.get(`/reservations/partner/settings/${venueId}`);
      setSettings(response.data.settings || {});
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const saveSettings = async () => {
    if (!selectedVenue) return;
    setSavingSettings(true);
    try {
      await api.put(`/reservations/partner/settings/${selectedVenue.id}`, {
        settings: settings
      });
      toast.success('Settings saved');
      setShowSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await api.put(`/reservations/${reservationId}/status`, { status: newStatus });
      
      if (newStatus === 'checked_in') {
        toast.success('Guest checked in!');
      } else if (newStatus === 'no_show') {
        toast.success('Marked as no-show. Card will be charged.');
      } else {
        toast.success(`Reservation ${newStatus}`);
      }
      
      fetchData();
      setShowDetailDialog(false);
    } catch (error) {
      toast.error('Failed to update reservation');
    }
  };

  // Calendar helpers
  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 0 })
  });

  const goToPreviousWeek = () => setCurrentWeekStart(addDays(currentWeekStart, -7));
  const goToNextWeek = () => setCurrentWeekStart(addDays(currentWeekStart, 7));
  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
    setSelectedDate(new Date());
  };

  const getReservationsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.filter(r => r.date === dateStr && r.status !== 'cancelled');
  };

  const filteredReservations = reservations.filter(r =>
    (r.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.venue_name?.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (selectedVenue ? r.venue_id === selectedVenue.id : true)
  );

  const filteredInquiries = inquiries.filter(i =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.venue_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Stats
  const todayReservations = reservations.filter(r => {
    try {
      return isSameDay(parseISO(r.date), new Date()) && r.status !== 'cancelled';
    } catch { return false; }
  });
  const pendingCount = reservations.filter(r => r.status === 'pending' || r.status === 'confirmed').length;
  const checkedInCount = reservations.filter(r => r.status === 'checked_in').length;
  const totalGuests = todayReservations.reduce((sum, r) => sum + (r.party_size || 0), 0);

  // Export reservations to CSV
  const exportToCSV = () => {
    const dataToExport = filteredReservations.length > 0 ? filteredReservations : reservations;
    
    if (dataToExport.length === 0) {
      toast.error('No reservations to export');
      return;
    }
    
    // CSV headers
    const headers = ['Customer Name', 'Email', 'Phone', 'Date', 'Time', 'Party Size', 'Type', 'Status', 'Special Requests', 'Venue', 'Confirmation #', 'Created At'];
    
    // Convert to CSV rows
    const rows = dataToExport.map(r => [
      r.user_name || '',
      r.user_email || '',
      r.user_phone || '',
      r.date || '',
      r.time || '',
      r.party_size || '',
      r.reservation_type || 'table',
      r.status || '',
      (r.special_requests || '').replace(/,/g, ';').replace(/\n/g, ' '),
      r.venue_name || '',
      r.id?.substring(0, 8) || '',
      r.created_at ? format(new Date(r.created_at), 'yyyy-MM-dd HH:mm') : ''
    ]);
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `reservations_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(`Exported ${dataToExport.length} reservations`);
  };

  if (!isAuthenticated || !isPartner) return null;

  return (
    <PartnerLayout 
      title="Reservations"
      subtitle="Manage customer bookings and inquiries"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
        </div>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-3xl font-bold">{todayReservations.length}</p>
                <p className="text-xs text-blue-100">Today's Reservations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalGuests}</p>
                <p className="text-xs text-muted-foreground">Expected Guests</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">Upcoming</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{checkedInCount}</p>
                <p className="text-xs text-muted-foreground">Checked In Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <CardHeader className="pb-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <TabsList>
                <TabsTrigger value="calendar">Calendar</TabsTrigger>
                <TabsTrigger value="list">List View</TabsTrigger>
                <TabsTrigger value="inquiries">
                  Private Hire
                  {inquiries.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{inquiries.length}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2">
                {venues.length > 1 && (
                  <Select value={selectedVenue?.id} onValueChange={(v) => {
                    const venue = venues.find(x => x.id === v);
                    setSelectedVenue(venue);
                    if (venue) fetchVenueSettings(venue.id);
                  }}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select venue" />
                    </SelectTrigger>
                    <SelectContent>
                      {venues.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search guests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Calendar View */}
            <TabsContent value="calendar" className="mt-0">
              <div className="space-y-4">
                {/* Calendar Navigation */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" onClick={goToPreviousWeek}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={goToNextWeek}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" onClick={goToToday}>Today</Button>
                  </div>
                  <h3 className="text-lg font-semibold">
                    {format(currentWeekStart, 'MMMM yyyy')}
                  </h3>
                </div>

                {/* Week Calendar */}
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((day) => {
                    const dayReservations = getReservationsForDate(day);
                    const isSelected = isSameDay(day, selectedDate);
                    
                    return (
                      <div
                        key={day.toISOString()}
                        className={`
                          p-3 rounded-lg border cursor-pointer transition-all min-h-[120px]
                          ${isToday(day) ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/20' : 'border-border'}
                          ${isSelected ? 'ring-2 ring-indigo-500' : ''}
                          hover:bg-muted/50
                        `}
                        onClick={() => setSelectedDate(day)}
                      >
                        <div className="text-center mb-2">
                          <p className="text-xs text-muted-foreground">{format(day, 'EEE')}</p>
                          <p className={`text-lg font-bold ${isToday(day) ? 'text-indigo-600' : ''}`}>
                            {format(day, 'd')}
                          </p>
                        </div>
                        
                        {dayReservations.length > 0 && (
                          <div className="space-y-1">
                            {dayReservations.slice(0, 3).map((r, idx) => (
                              <div 
                                key={r.id || idx}
                                className="text-xs p-1 bg-green-100 dark:bg-green-900/30 rounded truncate"
                                title={`${r.time} - ${r.user_name}`}
                              >
                                {r.time} • {r.party_size}p
                              </div>
                            ))}
                            {dayReservations.length > 3 && (
                              <p className="text-xs text-muted-foreground text-center">
                                +{dayReservations.length - 3} more
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Selected Day Reservations */}
                <Card className="mt-4">
                  <CardHeader className="py-3">
                    <CardTitle className="text-base">
                      {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                      {isToday(selectedDate) && <Badge className="ml-2 bg-indigo-600">Today</Badge>}
                    </CardTitle>
                    <CardDescription>
                      {getReservationsForDate(selectedDate).length} reservations
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="py-0 pb-4">
                    {getReservationsForDate(selectedDate).length > 0 ? (
                      <div className="space-y-3">
                        {getReservationsForDate(selectedDate)
                          .sort((a, b) => a.time.localeCompare(b.time))
                          .map((r) => (
                          <div 
                            key={r.id}
                            className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted cursor-pointer"
                            onClick={() => { setSelectedReservation(r); setShowDetailDialog(true); }}
                          >
                            <div className="flex items-center gap-4">
                              <div className="text-center min-w-[60px]">
                                <p className="text-lg font-bold text-indigo-600">{r.time}</p>
                              </div>
                              <div>
                                <p className="font-medium">{r.user_name}</p>
                                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {r.party_size} guests
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {r.user_phone || 'No phone'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={r.status} />
                              {r.status === 'confirmed' && (
                                <Button 
                                  size="sm" 
                                  onClick={(e) => { e.stopPropagation(); updateReservationStatus(r.id, 'checked_in'); }}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Check In
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-center py-8">
                        No reservations for this date
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* List View */}
            <TabsContent value="list" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Party</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Card</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No reservations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReservations.map((reservation) => (
                      <TableRow key={reservation.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedReservation(reservation); setShowDetailDialog(true); }}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{reservation.user_name}</p>
                            {reservation.special_requests && (
                              <p className="text-xs text-muted-foreground truncate max-w-[150px]" title={reservation.special_requests}>
                                {reservation.special_requests}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <a href={`tel:${reservation.user_phone}`} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                              <Phone className="w-3 h-3" />
                              {reservation.user_phone || 'N/A'}
                            </a>
                            <a href={`mailto:${reservation.user_email}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:underline" onClick={(e) => e.stopPropagation()}>
                              <Mail className="w-3 h-3" />
                              {reservation.user_email}
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{reservation.date}</p>
                          <p className="text-sm text-muted-foreground">{reservation.time}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            {reservation.party_size}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={reservation.status} />
                        </TableCell>
                        <TableCell>
                          {reservation.card_last_four ? (
                            <div className="flex items-center gap-1 text-sm">
                              <CreditCard className="w-4 h-4 text-muted-foreground" />
                              •••• {reservation.card_last_four}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">No card</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedReservation(reservation); setShowDetailDialog(true); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {reservation.status === 'confirmed' && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateReservationStatus(reservation.id, 'checked_in'); }} className="text-green-600">
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Check In
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              {reservation.status === 'confirmed' && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); updateReservationStatus(reservation.id, 'no_show'); }} className="text-red-600">
                                  <UserX className="w-4 h-4 mr-2" />
                                  Mark No-Show
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>

            {/* Inquiries Tab */}
            <TabsContent value="inquiries" className="mt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contact</TableHead>
                    <TableHead>Event Details</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInquiries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No private hire inquiries
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInquiries.map((inquiry) => (
                      <TableRow key={inquiry.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{inquiry.contact_name || inquiry.name}</p>
                            <p className="text-sm text-muted-foreground">{inquiry.contact_email || inquiry.email}</p>
                            {inquiry.contact_phone && (
                              <a href={`tel:${inquiry.contact_phone}`} className="text-sm text-indigo-600 hover:underline">
                                {inquiry.contact_phone}
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{inquiry.event_type}</p>
                          <p className="text-sm text-muted-foreground">{inquiry.expected_guests} guests</p>
                        </TableCell>
                        <TableCell>
                          {inquiry.preferred_date}
                        </TableCell>
                        <TableCell>
                          <Badge variant={inquiry.status === 'responded' ? 'default' : 'secondary'}>
                            {inquiry.status || 'New'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm" onClick={() => window.location.href = `mailto:${inquiry.contact_email || inquiry.email}`}>
                            <Mail className="w-4 h-4 mr-2" />
                            Reply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Reservation Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reservation Details</DialogTitle>
          </DialogHeader>
          
          {selectedReservation && (
            <div className="space-y-4">
              {/* Customer Contact Card */}
              <div className="bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg p-4 text-white">
                <p className="text-indigo-100 text-xs uppercase tracking-wider mb-1">Customer</p>
                <p className="text-xl font-bold">{selectedReservation.user_name}</p>
                <div className="mt-3 space-y-2">
                  <a href={`tel:${selectedReservation.user_phone}`} className="flex items-center gap-2 hover:underline">
                    <Phone className="w-4 h-4" />
                    {selectedReservation.user_phone || 'No phone'}
                  </a>
                  <a href={`mailto:${selectedReservation.user_email}`} className="flex items-center gap-2 hover:underline">
                    <Mail className="w-4 h-4" />
                    {selectedReservation.user_email}
                  </a>
                </div>
              </div>

              {/* Reservation Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Date & Time</p>
                  <p className="font-bold">{selectedReservation.date}</p>
                  <p className="text-indigo-600 font-medium">{selectedReservation.time}</p>
                </div>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">Party Size</p>
                  <p className="font-bold text-2xl">{selectedReservation.party_size}</p>
                  <p className="text-sm text-muted-foreground">guests</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={selectedReservation.status} />
                </div>
                {selectedReservation.card_last_four && (
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Card on File</p>
                    <p className="flex items-center gap-1">
                      <CreditCard className="w-4 h-4" />
                      •••• {selectedReservation.card_last_four}
                    </p>
                  </div>
                )}
              </div>

              {/* Special Requests */}
              {selectedReservation.special_requests && (
                <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1">Special Requests</p>
                  <p className="text-sm">{selectedReservation.special_requests}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                {selectedReservation.status === 'confirmed' && (
                  <>
                    <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => updateReservationStatus(selectedReservation.id, 'checked_in')}>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Check In
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => updateReservationStatus(selectedReservation.id, 'no_show')}>
                      <UserX className="w-4 h-4 mr-2" />
                      No-Show
                    </Button>
                  </>
                )}
                {selectedReservation.status === 'checked_in' && (
                  <div className="flex-1 p-3 bg-green-100 dark:bg-green-900/30 rounded-lg text-center">
                    <Check className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-green-700 dark:text-green-400 font-medium">Guest Checked In</p>
                    {selectedReservation.checked_in_at && (
                      <p className="text-xs text-green-600">at {format(new Date(selectedReservation.checked_in_at), 'h:mm a')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reservation Settings</DialogTitle>
            <DialogDescription>
              Configure how reservations work for {selectedVenue?.name || 'your venue'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Card Hold */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Require Card Hold</Label>
                <p className="text-xs text-muted-foreground">
                  Hold customer's card to prevent no-shows
                </p>
              </div>
              <Switch
                checked={settings.require_card_hold}
                onCheckedChange={(checked) => setSettings({...settings, require_card_hold: checked})}
              />
            </div>

            {/* No-show Fee */}
            {settings.require_card_hold && (
              <div className="space-y-2">
                <Label>No-Show Fee (per person)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={settings.no_show_fee}
                    onChange={(e) => setSettings({...settings, no_show_fee: parseFloat(e.target.value) || 0})}
                    className="pl-7"
                    min="0"
                    step="5"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Amount charged per person if guest doesn't show up
                </p>
              </div>
            )}

            {/* Cancellation Window */}
            <div className="space-y-2">
              <Label>Cancellation Window (hours)</Label>
              <Select 
                value={String(settings.cancellation_hours)} 
                onValueChange={(v) => setSettings({...settings, cancellation_hours: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 hours before</SelectItem>
                  <SelectItem value="6">6 hours before</SelectItem>
                  <SelectItem value="12">12 hours before</SelectItem>
                  <SelectItem value="24">24 hours before</SelectItem>
                  <SelectItem value="48">48 hours before</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Guests must cancel this far in advance to avoid fees
              </p>
            </div>

            {/* Advance Booking */}
            <div className="space-y-2">
              <Label>Advance Booking Window (days)</Label>
              <Select 
                value={String(settings.advance_booking_days)} 
                onValueChange={(v) => setSettings({...settings, advance_booking_days: parseInt(v)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How far in advance guests can book
              </p>
            </div>

            {/* Confirmation Message */}
            <div className="space-y-2">
              <Label>Custom Confirmation Message</Label>
              <Textarea
                value={settings.confirmation_message}
                onChange={(e) => setSettings({...settings, confirmation_message: e.target.value})}
                placeholder="e.g., Please arrive 10 minutes early..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Shown to guests after they complete a booking
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveSettings} disabled={savingSettings}>
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
};

export default PartnerReservations;
